/**
 * 问答式问卷组件
 */
class WDQuiz extends HTMLElement {
  constructor() {
    super()

    // DOM 元素引用
    this.questions = null // 所有问题元素的 NodeList
    this.nextButton = null // 下一题/查看结果按钮
    this.backButton = null // 返回上一题按钮
    this.startButton = null // 开始问卷按钮
    this.container = null // 问卷卡片轨道容器（用于水平滑动）
    this.questionsContainer = null // 问题容器（包含所有问题，用于横向滚动）
    this.progressBar = null // 进度条元素（显示进度百分比）
    this.progressBarContainer = null // 进度条容器（设置 aria 属性）
    this.cards = null // 卡片内容容器（控制开始页和问题页的显示）

    this.answeredQuestions = [] // 已回答问题列表（保存完整答案对象）
    this.currentSelection = null // 当前选择的答案（临时状态，未确认）
    this.currentQuestionId = null
    this.currentIndex = null
    this.questionSet = []
    this.selectionPath = [] // 用于匹配产品的路径数组（不包含可选问题）

    this.questionIndexMap = null

    this.boundHandlers = {
      onStartClick: null,
      onOptionClick: null,
      onNextClick: null,
      onBackClick: null
    }

    this.ignoredQuestionTypes = new Set(['zip_code'])
  }

  connectedCallback() {
    this.initializeQuiz()
    this.attachEventListeners()
  }

  disconnectedCallback() {
    this.detachEventListeners()
  }

  initializeQuiz() {
    this.questions = this.querySelectorAll('.quiz-question')
    this.nextButton = this.querySelector('.btn-next')
    this.backButton = this.querySelector('.btn-back')
    this.startButton = this.querySelector('.start-button')
    this.container = this.querySelector('.quiz-answer-card-track')
    this.questionsContainer = this.querySelector('.quiz-answer-card-questions')
    this.progressBar = this.querySelector('.quiz-answer-card-body-progress')
    this.progressBarContainer = this.querySelector('.quiz-answer-card-body-progress-bar')
    this.cards = this.querySelector('.quiz-answer-card-content')

    this.questionIndexMap = new Map()
    this.questions.forEach((question, index) => {
      const { questionId, title } = question.dataset
      if (questionId) this.questionIndexMap.set(questionId, index)
      if (!this.ignoredQuestionTypes.has(question.dataset.type)) this.questionSet.push(title)
    })
  }

  attachEventListeners() {
    // 绑定 start button 点击事件
    this.boundHandlers.onStartClick = this.handleStartQuiz.bind(this)
    this.startButton?.addEventListener('click', this.boundHandlers.onStartClick)

    if (!this.questionsContainer || !this.nextButton || !this.backButton) {
      return
    }

    this.boundHandlers.onOptionClick = this.handleOptionClick.bind(this)
    this.questionsContainer.addEventListener('click', this.boundHandlers.onOptionClick)

    this.boundHandlers.onNextClick = this.handleNextQuestion.bind(this)
    this.nextButton.addEventListener('click', this.boundHandlers.onNextClick)

    this.boundHandlers.onBackClick = this.handleGoBack.bind(this)
    this.backButton.addEventListener('click', this.boundHandlers.onBackClick)
  }

  detachEventListeners() {
    this.startButton?.removeEventListener('click', this.boundHandlers.onStartClick)
    this.questionsContainer?.removeEventListener('click', this.boundHandlers.onOptionClick)
    this.nextButton?.removeEventListener('click', this.boundHandlers.onNextClick)
    this.backButton?.removeEventListener('click', this.boundHandlers.onBackClick)

    this.boundHandlers = {
      onStartClick: null,
      onOptionClick: null,
      onNextClick: null,
      onBackClick: null
    }
  }

  handleStartQuiz() {
    if (!this.container || this.questions.length === 0) return

    this.cards.classList.remove('hidden')
    this.container.style.transform = 'translateX(-100%)'

    const firstQuestion = this.questions[0]
    this.currentQuestionId = firstQuestion.dataset.questionId || null
    this.currentIndex = 0

    this.updateProgress()
    this.showCurrentQuestion()
  }

  handleOptionClick(event) {
    const option = event.target.closest('.quiz-answer-card-body-option')
    if (!option || option.classList.contains('selected')) {
      return
    }

    const questionElement = option.closest('.quiz-question')
    if (!questionElement) return

    this.deselectAllOptionsInQuestion(questionElement)
    this.selectOption(option)

    this.currentSelection = {
      element: option,
      answer: option.dataset.answer,
      questionId: option.dataset.relatedQuestion,
      nextQuestionId: option.dataset.nextQuestion,
      condition: option.dataset.condition || null
    }

    this.updateButtonStates()
  }

  /**
   * 取消选中问题中的所有选项
   */
  deselectAllOptionsInQuestion(questionElement) {
    const options = questionElement.querySelectorAll('.quiz-answer-card-body-option')
    options.forEach(opt => {
      opt.classList.remove('selected')
      opt.setAttribute('aria-pressed', 'false')
    })
  }

  /**
   * 选中指定选项
   */
  selectOption(option) {
    option.classList.add('selected')
    option.setAttribute('aria-pressed', 'true')
  }

  /**
   * 获取问题的索引
   */
  getQuestionIndex(questionId) {
    return this.questionIndexMap?.get(questionId) ?? -1
  }

  /**
   * 获取问题元素
   */
  getQuestionById(questionId) {
    const questionIndex = this.getQuestionIndex(questionId)
    return questionIndex === -1 ? null : this.questions[questionIndex]
  }

  /**
   * 判断问题是否应跳过
   */
  shouldIgnoreQuestion(questionId) {
    const question = this.getQuestionById(questionId)
    return this.ignoredQuestionTypes.has(question?.dataset.type)
  }

  /**
   * 从当前选项的 dataset.condition 中解析配置
   * data-condition 格式示例: `{"nextConditions": [{"condition": {"q2": "Ultrafiltration"}, "nextQuestionId": "q6"}]}`
   */
  findAnswerConfig() {
    if (!this.currentSelection?.element) return null

    const conditionStr = this.currentSelection.element.dataset.condition
    if (!conditionStr || conditionStr === '{}' || conditionStr.trim() === '') {
      return null
    }

    try {
      const condition = JSON.parse(conditionStr)
      return condition
    } catch (error) {
      console.warn('Failed to parse condition:', error)
      return null
    }
  }

  /**
   * 根据 nextConditions 决定下一个问题ID
   * 逻辑: 从 data-condition 中解析 nextConditions，如果存在则按顺序检查条件匹配，找到第一个匹配的返回其 nextQuestionId
   *       否则使用 data-next-question 的值
   */
  getNextQuestionId() {
    if (!this.currentSelection) return null

    const { nextQuestionId } = this.currentSelection
    const answerConfig = this.findAnswerConfig()
    let resolvedNextQuestionId = nextQuestionId
    if (!answerConfig?.nextConditions?.length) {
      return this.shouldIgnoreQuestion(resolvedNextQuestionId) ? null : resolvedNextQuestionId
    }
    for (const nextCondition of answerConfig.nextConditions) {
      if (this.isConditionMatched(nextCondition.condition)) {
        resolvedNextQuestionId = nextCondition.nextQuestionId
        break
      }
    }
    return this.shouldIgnoreQuestion(resolvedNextQuestionId) ? null : resolvedNextQuestionId
  }

  /**
   * 处理返回操作
   */
  handleGoBack() {
    if (this.currentSelection?.element) {
      this.clearCurrentSelection()
    }

    if (this.answeredQuestions.length === 0) {
      this.resetToInitialState()
      return
    }

    const previousAnswer = this.answeredQuestions.pop()
    this.currentQuestionId = previousAnswer.questionId
    this.currentSelection = previousAnswer
    this.selectionPath.pop()
    this.currentIndex = this.getQuestionIndex(this.currentQuestionId)
    this.showCurrentQuestion()
    this.updateButtonStates()
    this.updateProgress()
  }

  /**
   * 清除当前选择状态
   */
  clearCurrentSelection() {
    const { element } = this.currentSelection
    element.classList.remove('selected')
    element.setAttribute('aria-pressed', 'false')
  }

  /**
   * 重置到初始状态
   */
  resetToInitialState() {
    if (this.container) this.container.style.transform = 'translateX(0%)'
    if (this.cards) this.cards.classList.add('hidden')
    this.questions?.forEach(question => question.classList.remove('current-quiz-question'))
    this.currentSelection = null
    this.currentIndex = -1
    this.updateButtonStates()
    this.updateProgress()
  }

  /**
   * 处理下一题操作
   */
  handleNextQuestion() {
    if (!this.currentSelection) return

    const nextQuestionId = this.getNextQuestionId()
    const currentQuestion = this.questions[this.currentIndex]

    this.answeredQuestions.push({ ...this.currentSelection })
    if (currentQuestion.dataset.isOptional === 'false') this.selectionPath.push(this.currentSelection.answer)
    else this.selectionPath.push('')

    if (!nextQuestionId) {
      this.saveResultAndRedirect()
      return
    }

    this.currentQuestionId = nextQuestionId
    this.currentSelection = null
    this.currentIndex = this.getQuestionIndex(nextQuestionId)

    this.showCurrentQuestion()
    this.updateButtonStates()
    this.updateProgress()
  }

  /**
   * 显示当前问题
   */
  showCurrentQuestion() {
    if (!this.questionsContainer || this.currentIndex === null || this.currentIndex === -1) {
      return
    }

    this.questions.forEach((question, index) => {
      question.classList.toggle('current-quiz-question', index === this.currentIndex)
    })

    const translateX = -this.currentIndex * 100
    this.questionsContainer.style.transform = `translateX(${translateX}%)`

    this.updateCurrentQuestionVisibility()
  }

  /**
   * 检查答案条件是否满足（hideConditions 优先级最高，showConditions 次之，都无则默认显示）
   */
  checkAnswerCondition(conditionStr) {
    if (!conditionStr || conditionStr === '{}' || conditionStr.trim() === '') {
      return true
    }

    try {
      const condition = JSON.parse(conditionStr)
      const { showConditions = [], hideConditions = [] } = condition

      if (hideConditions.length > 0) {
        const isHidden = hideConditions.some(hideCond => this.isConditionMatched(hideCond))
        if (isHidden) return false
      }

      if (showConditions.length > 0) {
        return showConditions.some(showCond => this.isConditionMatched(showCond))
      }

      return true
    } catch {
      return false
    }
  }

  /**
   * 检查条件是否匹配
   */
  isConditionMatched(condition) {
    if (!condition || typeof condition !== 'object') return false

    const answerMap = new Map()
    this.answeredQuestions.forEach(answer => {
      if (answer.questionId) answerMap.set(answer.questionId, answer.answer)
    })

    for (const [questionId, expectedAnswer] of Object.entries(condition)) {
      const actualAnswer = answerMap.get(questionId)
      if (actualAnswer !== expectedAnswer) return false
    }

    return true
  }

  /**
   * 更新当前问题的选项可见性
   */
  updateCurrentQuestionVisibility() {
    if (this.currentIndex === null || this.currentIndex === -1) return

    const currentQuestion = this.questions[this.currentIndex]
    if (!currentQuestion) return

    const options = currentQuestion.querySelectorAll('.quiz-answer-card-body-option')
    options.forEach(option => {
      const conditionStr = option.dataset.condition || ''
      const isVisible = this.checkAnswerCondition(conditionStr)
      option.classList.toggle('quiz-option--hidden', !isVisible)
      option.ariaHidden = !isVisible
    })
  }

  /**
   * 更新按钮状态
   */
  updateButtonStates() {
    if (!this.nextButton) return

    const nextQuestionId = this.currentSelection ? this.getNextQuestionId() : null
    const isLastQuestion = this.currentIndex === this.questions.length - 1 || (this.currentSelection && !nextQuestionId)
    this.nextButton.textContent = isLastQuestion ? 'See Result' : 'Next'
    this.nextButton.disabled = !this.currentSelection || !this.currentSelection.answer
  }

  /**
   * 计算进度百分比
   */
  calculateProgress() {
    if (!this.questionSet.length) return 0
    const current = this.questions[this.currentIndex]?.dataset?.title
    const answeredCount = this.questionSet.indexOf(current) + 1
    return Math.min((answeredCount / this.questionSet.length) * 100, 100)
  }

  /**
   * 更新进度条显示
   */
  updateProgress() {
    if (!this.progressBarContainer) return

    const progress = this.calculateProgress()
    const roundedProgress = Math.round(progress)

    this.progressBarContainer.setAttribute('aria-valuenow', roundedProgress)
    if (this.progressBar) {
      this.progressBar.style.width = `${progress}%`
    }
  }

  /**
   * 保存结果并跳转到结果页
   */
  saveResultAndRedirect() {
    const quizId = this.dataset.quizId || 'quiz'
    const storageKey = `WD-${quizId}`
    const pathString = JSON.stringify([...this.selectionPath.filter(item => item !== '')])
    const encryptedPath = btoa(encodeURIComponent(pathString))
    this.nextButton.disabled = true
    this.nextButton.classList.add('loading')

    try {
      localStorage.setItem(storageKey, encryptedPath)
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
    }

    const url = this.dataset.redirectUrl || '/pages/water-quality-report'

    window.location.href = url
  }
}

// 注册自定义元素
if (!customElements.get('wd-quiz')) {
  customElements.define('wd-quiz', WDQuiz)
}
